export interface Audience {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
  len: number;
  total_personas?: number;
  segments?: Segment[];
  website?: string;
  business_description?: string;
  industry?: string;
  core_need?: string;
  additional_info?: string;
  audience_purpose?: string;
  customers?: string;
  location?: string;
  age_range?: string;
  income_level?: string;
}

export interface Segment {
  id: number;
  name: string;
  persona_count: number;
  sub_industry?: string;
}

export interface PaginatedAudiences {
  audiences: Audience[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

import { performanceMonitor } from '../utils/performanceMonitor';

export interface AudienceFilters {
  search?: string;
  industry?: string;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

class AudienceService {
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
  private readonly defaultTTL = 5 * 60 * 1000; // 5 minutes
  private readonly API_URL = import.meta.env.VITE_API_URL || '';
  private activeRequests = new Map<string, Promise<any>>();

  private getCacheKey(type: string, params?: any): string {
    return `${type}_${JSON.stringify(params || {})}`;
  }

  private isValidCache(entry: { timestamp: number; ttl: number }): boolean {
    return Date.now() - entry.timestamp < entry.ttl;
  }

  private async fetchWithDeduplication<T>(
    key: string,
    fetcher: () => Promise<T>,
    ttl: number = this.defaultTTL
  ): Promise<T> {
    // Check cache first
    const cached = this.cache.get(key);
    if (cached && this.isValidCache(cached)) {
      return cached.data;
    }

    // Check if request is already in progress
    if (this.activeRequests.has(key)) {
      return this.activeRequests.get(key);
    }

    // Start new request
    const promise = fetcher().then((data) => {
      // Cache the result
      this.cache.set(key, {
        data,
        timestamp: Date.now(),
        ttl
      });
      // Remove from active requests
      this.activeRequests.delete(key);
      return data;
    }).catch((error) => {
      // Remove from active requests on error
      this.activeRequests.delete(key);
      throw error;
    });

    this.activeRequests.set(key, promise);
    return promise;
  }

  async fetchAudienceIds(): Promise<number[]> {
    const key = this.getCacheKey('audience_ids');
    performanceMonitor.startLoading('fetch_audience_ids');
    
    try {
      const result = await this.fetchWithDeduplication(key, async () => {
        const response = await fetch(`${this.API_URL}/audience/ids`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch audience IDs');
        }
        
        return response.json();
      });
      
      performanceMonitor.endLoading('fetch_audience_ids', true, { 
        count: result.length,
        cacheHit: this.cache.has(key) && this.isValidCache(this.cache.get(key)!)
      });
      
      return result;
    } catch (error) {
      performanceMonitor.recordError('fetch_audience_ids', error as Error);
      throw error;
    }
  }

  async fetchAudience(audienceId: number): Promise<Audience> {
    const key = this.getCacheKey('audience', audienceId);
    
    return this.fetchWithDeduplication(key, async () => {
      const response = await fetch(`${this.API_URL}/audience/${audienceId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch audience ${audienceId}`);
      }
      
      return response.json();
    });
  }

  async fetchAudiencesParallel(audienceIds: number[], concurrency: number = 5): Promise<Audience[]> {
    const operationId = `fetch_audiences_parallel_${audienceIds.length}`;
    performanceMonitor.measureAudienceLoading(operationId, audienceIds.length);
    
    const results: Audience[] = [];
    const errors: { id: number; error: Error }[] = [];
    const startTime = Date.now();

    try {
      // Process in batches to control concurrency
      for (let i = 0; i < audienceIds.length; i += concurrency) {
        const batch = audienceIds.slice(i, i + concurrency);
        const batchPromises = batch.map(async (id) => {
          try {
            return await this.fetchAudience(id);
          } catch (error) {
            errors.push({ id, error: error as Error });
            return null;
          }
        });

        const batchResults = await Promise.allSettled(batchPromises);
        batchResults.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value) {
            results.push(result.value);
          }
        });
      }

      if (errors.length > 0) {
        console.warn(`Failed to fetch ${errors.length} audiences:`, errors);
      }

      const sortedResults = results.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      
      // Calculate cache hits
      const cacheHits = audienceIds.filter(id => {
        const key = this.getCacheKey('audience', id);
        return this.cache.has(key) && this.isValidCache(this.cache.get(key)!);
      }).length;
      
      performanceMonitor.measureAudienceLoadingComplete(
        operationId, 
        true, 
        sortedResults.length, 
        cacheHits > 0
      );
      
      performanceMonitor.measureCacheHit('audience_parallel', cacheHits, audienceIds.length);
      
      return sortedResults;
    } catch (error) {
      performanceMonitor.recordError(operationId, error as Error, { 
        audienceCount: audienceIds.length,
        errorCount: errors.length 
      });
      throw error;
    }
  }

  async fetchAudiencesPaginated(
    page: number = 1,
    pageSize: number = 20,
    filters?: AudienceFilters
  ): Promise<PaginatedAudiences> {
    const cacheKey = this.getCacheKey('audiences_paginated', { page, pageSize, filters });
    
    return this.fetchWithDeduplication(cacheKey, async () => {
      // First get all audience IDs
      const allIds = await this.fetchAudienceIds();
      
      let filteredIds = allIds;
      
      // If we have filters and need to apply search, we'll need to fetch some data
      if (filters?.search || filters?.industry) {
        // For search/filter, we need to load audience data to filter
        // Load all audiences in batches and then filter
        const allAudiences = await this.fetchAudiencesParallel(allIds);
        
        let filtered = allAudiences;
        
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          filtered = filtered.filter(audience => 
            audience.name.toLowerCase().includes(searchLower) ||
            audience.business_description?.toLowerCase().includes(searchLower) ||
            audience.industry?.toLowerCase().includes(searchLower)
          );
        }
        
        if (filters.industry) {
          filtered = filtered.filter(audience => 
            audience.industry?.toLowerCase().includes(filters.industry!.toLowerCase())
          );
        }
        
        if (filters.dateRange) {
          filtered = filtered.filter(audience => {
            const createdDate = new Date(audience.created_at);
            return createdDate >= filters.dateRange!.start && createdDate <= filters.dateRange!.end;
          });
        }
        
        filteredIds = filtered.map(a => a.id);
      }
      
      // Apply pagination
      const total = filteredIds.length;
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedIds = filteredIds.slice(startIndex, endIndex);
      
      // Fetch the actual audience data for this page
      const audiences = await this.fetchAudiencesParallel(paginatedIds);
      
      return {
        audiences,
        total,
        page,
        pageSize,
        hasMore: endIndex < total
      };
    }, this.defaultTTL / 2); // Shorter cache for paginated results
  }

  async* fetchAudiencesProgressive(
    page: number = 1,
    pageSize: number = 20,
    filters?: AudienceFilters,
    batchSize: number = 2
  ): AsyncGenerator<{ audience: Audience; index: number; total: number }, void, unknown> {
    // First get all audience IDs
    const allIds = await this.fetchAudienceIds();
    
    let filteredIds = allIds;
    let cachedAudiences: Map<number, Audience> = new Map();
    
    // If we have filters, we need to fetch and filter
    if (filters?.search || filters?.industry || filters?.dateRange) {
      // Check cache for filtered results
      const filterCacheKey = this.getCacheKey('filtered_ids', filters);
      const cachedFiltered = this.cache.get(filterCacheKey);
      
      if (cachedFiltered && this.isValidCache(cachedFiltered)) {
        filteredIds = cachedFiltered.data.ids;
        cachedAudiences = new Map(cachedFiltered.data.audiences);
      } else {
        // Load all audiences to filter
        const allAudiences = await this.fetchAudiencesParallel(allIds);
        
        let filtered = allAudiences;
        
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          filtered = filtered.filter(audience => 
            audience.name.toLowerCase().includes(searchLower) ||
            audience.business_description?.toLowerCase().includes(searchLower) ||
            audience.industry?.toLowerCase().includes(searchLower)
          );
        }
        
        if (filters.industry) {
          filtered = filtered.filter(audience => 
            audience.industry?.toLowerCase().includes(filters.industry!.toLowerCase())
          );
        }
        
        if (filters.dateRange) {
          filtered = filtered.filter(audience => {
            const createdDate = new Date(audience.created_at);
            return createdDate >= filters.dateRange!.start && createdDate <= filters.dateRange!.end;
          });
        }
        
        filteredIds = filtered.map(a => a.id);
        
        // Cache the filtered results
        filtered.forEach(a => cachedAudiences.set(a.id, a));
        this.cache.set(filterCacheKey, {
          data: { ids: filteredIds, audiences: Array.from(cachedAudiences.entries()) },
          timestamp: Date.now(),
          ttl: this.defaultTTL / 2
        });
      }
    }
    
    // Apply pagination
    const totalFiltered = filteredIds.length;
    const startIndex = (page - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalFiltered);
    const paginatedIds = filteredIds.slice(startIndex, endIndex);
    
    // Yield audiences progressively
    for (let i = 0; i < paginatedIds.length; i += batchSize) {
      const batch = paginatedIds.slice(i, Math.min(i + batchSize, paginatedIds.length));
      
      const batchPromises = batch.map(async (id, batchIndex) => {
        // Check if we already have this audience cached
        if (cachedAudiences.has(id)) {
          return { audience: cachedAudiences.get(id)!, index: i + batchIndex };
        }
        
        // Otherwise fetch it
        try {
          const audience = await this.fetchAudience(id);
          return { audience, index: i + batchIndex };
        } catch (error) {
          console.error(`Failed to fetch audience ${id}:`, error);
          return null;
        }
      });
      
      const results = await Promise.all(batchPromises);
      
      for (const result of results) {
        if (result) {
          yield {
            audience: result.audience,
            index: result.index,
            total: totalFiltered
          };
        }
      }
    }
  }

  async fetchAllAudiences(): Promise<Audience[]> {
    const cacheKey = this.getCacheKey('all_audiences');
    
    return this.fetchWithDeduplication(cacheKey, async () => {
      const ids = await this.fetchAudienceIds();
      return this.fetchAudiencesParallel(ids);
    });
  }

  // Utility methods for cache management
  clearCache(): void {
    this.cache.clear();
    this.activeRequests.clear();
  }

  invalidateAudience(audienceId: number): void {
    const keys = Array.from(this.cache.keys()).filter(key => 
      key.includes(`audience_${audienceId}`) || key.includes('all_audiences') || key.includes('audiences_paginated')
    );
    keys.forEach(key => this.cache.delete(key));
  }

  prefetchAudiences(audienceIds: number[]): Promise<Audience[]> {
    return this.fetchAudiencesParallel(audienceIds);
  }

  // Background refresh
  async refreshCache(): Promise<void> {
    try {
      await this.fetchAudienceIds();
    } catch (error) {
      console.warn('Failed to refresh audience cache:', error);
    }
  }

  // Get cache stats for monitoring
  getCacheStats(): { size: number; keys: string[]; activeRequests: number } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      activeRequests: this.activeRequests.size
    };
  }
}

class AudienceServiceManager {
  private service: AudienceService;
  private refreshInterval: NodeJS.Timeout | null = null;

  constructor() {
    this.service = new AudienceService();
  }

  getInstance(): AudienceService {
    return this.service;
  }

  startAutoRefresh(): void {
    // Clean up any existing interval
    this.stopAutoRefresh();
    
    // Start auto-refresh every 10 minutes
    if (typeof window !== 'undefined') {
      this.refreshInterval = setInterval(() => {
        this.service.refreshCache();
      }, 10 * 60 * 1000);
    }
  }

  stopAutoRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }
}

const audienceServiceManager = new AudienceServiceManager();
export const audienceService = audienceServiceManager.getInstance();

// Start auto-refresh but provide cleanup mechanism
audienceServiceManager.startAutoRefresh();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    audienceServiceManager.stopAutoRefresh();
  });
}